import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';

const API_BASE = '/api/stand-counts';

const CONDITION_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'OK', label: 'Sağlam' },
  { key: 'FaultRequested', label: 'Arıza Bildirildi' },
  { key: 'Replaced', label: 'Değiştirildi' },
];

const CONDITION_META = {
  OK: { label: 'Sağlam', tone: 'ok' },
  FaultRequested: { label: 'Arıza Bildirildi', tone: 'bad' },
  Replaced: { label: 'Değiştirildi', tone: 'warn' },
};

// StandCountItem.PlacementType — Shelf/Door/Top/Other.
const PLACEMENT_LABELS = {
  Shelf: 'Raf',
  Door: 'Kapak',
  Top: 'Üst',
  Other: 'Diğer',
};

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    okCount: pick(data, 'okCount', 'OkCount') ?? 0,
    faultCount: pick(data, 'faultCount', 'FaultCount') ?? 0,
    replacedCount: pick(data, 'replacedCount', 'ReplacedCount') ?? 0,
  };
}

function placementLabel(p) {
  if (!p) return '-';
  return PLACEMENT_LABELS[p] || p;
}

const TONES = {
  ok: { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' },
  warn: { color: '#92400e', background: 'rgba(253, 171, 61, 0.14)', borderColor: 'rgba(253, 171, 61, 0.32)' },
  bad: { color: '#7f1d1d', background: 'rgba(226, 68, 92, 0.12)', borderColor: 'rgba(226, 68, 92, 0.30)' },
  neutral: { color: '#64748b', background: 'rgba(100, 116, 139, 0.14)', borderColor: 'rgba(100, 116, 139, 0.28)' },
};

function conditionPillFor(condition) {
  const meta = CONDITION_META[condition] || { label: condition || '-', tone: 'neutral' };
  const t = TONES[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      border: `1px solid ${t.borderColor}`, background: t.background, color: t.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor', opacity: 0.85 }} />
      {meta.label}
    </span>
  );
}

// Soğutucu ideal aralık 2-8°C — dışına çıkınca kırmızı/turuncu uyarı tonu.
function tempPill(value) {
  if (value === undefined || value === null || value === '') return <span style={{ color: '#94a3b8' }}>-</span>;
  const t = Number(value);
  if (!Number.isFinite(t)) return <span style={{ color: '#94a3b8' }}>-</span>;
  let tone = TONES.ok;
  if (t < 2 || t > 8) tone = TONES.bad;
  else if (t < 3 || t > 7) tone = TONES.warn;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 9px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      border: `1px solid ${tone.borderColor}`, background: tone.background, color: tone.color,
      whiteSpace: 'nowrap',
    }}>
      {t.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}°C
    </span>
  );
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function toIsoStart(dateStr) {
  if (!dateStr) return '';
  try { return new Date(`${dateStr}T00:00:00`).toISOString(); } catch { return ''; }
}

function toIsoEnd(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  } catch { return ''; }
}

function badge(label, value, tone) {
  const tones = {
    blue: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8', dot: 'var(--accent)' },
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    red: { bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)', color: '#7f1d1d', dot: '#dc2626' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function StandCountsPageLegacy() {
  const [conditionFilter, setConditionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ okCount: 0, faultCount: 0, replacedCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('CountedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [detailRow, setDetailRow] = useState(null);
  const [detailItems, setDetailItems] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (conditionFilter) params.set('condition', conditionFilter);
      const isoFrom = toIsoStart(dateFrom);
      const isoTo = toIsoEnd(dateTo);
      if (isoFrom) params.set('from', isoFrom);
      if (isoTo) params.set('to', isoTo);
      params.set('take', '500');

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ okCount: data.okCount, faultCount: data.faultCount, replacedCount: data.replacedCount });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [conditionFilter, dateFrom, dateTo, showToast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setDetailRow(row);
    setDetailItems(null);
    setDetailLoading(true);
    try {
      const id = pick(row, 'id', 'Id');
      const res = await fetch(`${API_BASE}/${id}/items`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Kalemler alınamadı (${res.status})`);
      const payload = await res.json();
      const data = pick(payload, 'Data', 'data') ?? payload;
      setDetailItems(pick(data, 'items', 'Items') ?? []);
    } catch (err) {
      showToast('error', err.message || 'Kalemler alınamadı');
      setDetailItems([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'customerTitle', 'CustomerTitle'),
        pick(row, 'standCode', 'StandCode'),
        pick(row, 'standType', 'StandType'),
        pick(row, 'notes', 'Notes'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'StandType': return pick(row, 'standType', 'StandType') || '';
        case 'Condition': return pick(row, 'condition', 'Condition') || '';
        case 'TotalQuantity': return Number(pick(row, 'totalQuantity', 'TotalQuantity')) || 0;
        case 'CountedAt':
        default: {
          const v = pick(row, 'countedAt', 'CountedAt');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'countedAt',
      headerName: 'Sayım Tarihi',
      flex: 0.9,
      minWidth: 150,
      valueGetter: ({ row }) => formatDate(pick(row, 'countedAt', 'CountedAt')),
    },
    {
      field: 'customerTitle',
      headerName: 'Müşteri',
      flex: 1.3,
      minWidth: 190,
      valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '-',
    },
    {
      field: 'standCode',
      headerName: 'Soğutucu',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => {
        const code = pick(row, 'standCode', 'StandCode');
        const type = pick(row, 'standType', 'StandType');
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{code || '-'}</div>
            {type && <div style={{ fontSize: 11, color: '#6b7280' }}>{type}</div>}
          </div>
        );
      },
    },
    {
      field: 'temperatureCelsius',
      headerName: 'Sıcaklık',
      flex: 0.6,
      minWidth: 100,
      renderCell: ({ row }) => tempPill(pick(row, 'temperatureCelsius', 'TemperatureCelsius')),
    },
    {
      field: 'condition',
      headerName: 'Durum',
      flex: 0.9,
      minWidth: 150,
      renderCell: ({ row }) => conditionPillFor(pick(row, 'condition', 'Condition')),
    },
    {
      field: 'totalQuantity',
      headerName: 'Toplam Adet',
      flex: 0.6,
      minWidth: 110,
      valueGetter: ({ row }) => pick(row, 'totalQuantity', 'TotalQuantity') ?? 0,
    },
    {
      field: 'itemCount',
      headerName: 'Kalem',
      flex: 0.5,
      minWidth: 80,
      valueGetter: ({ row }) => pick(row, 'itemCount', 'ItemCount') ?? 0,
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--accent)' }}
            onClick={() => openDetail(row)}
          >
            Detay
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Soğutucu Sayımları</h1>
          <p style={s.pageSubtitle}>
            Saha temsilcisinin müşteri soğutucu/standlarında yaptığı içerik sayımları —
            sıcaklık ölçümü, durum ve ürün kalemleri. Mobile'dan push ile gelir; admin görüntüleme.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {badge('Sağlam', counts.okCount, 'green')}
          {badge('Arıza', counts.faultCount, 'red')}
          {badge('Değiştirildi', counts.replacedCount, 'orange')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {CONDITION_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setConditionFilter(tab.key)}
              style={{
                ...(conditionFilter === tab.key ? s.btnPrimary : s.btnSecondary),
                padding: '6px 14px', fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div style={s.toolbarRight}>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ ...s.filterInput, width: 140 }}
            title="Başlangıç"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ ...s.filterInput, width: 140 }}
            title="Bitiş"
          />
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Müşteri / soğutucu kodu..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={openDetail}
        exportFileName="sogutucu-sayim"
      />

      {detailRow && (
        <div style={s.overlay} onClick={() => { setDetailRow(null); setDetailItems(null); }}>
          <div style={{ ...s.modal, maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {pick(detailRow, 'customerTitle', 'CustomerTitle') || 'Soğutucu Sayımı'}
                {pick(detailRow, 'standCode', 'StandCode') ? ` • ${pick(detailRow, 'standCode', 'StandCode')}` : ''}
              </h2>
              <button
                type="button"
                style={s.closeBtn}
                onClick={() => { setDetailRow(null); setDetailItems(null); }}
              >×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div>{conditionPillFor(pick(detailRow, 'condition', 'Condition'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Sıcaklık</label>
                  <div>{tempPill(pick(detailRow, 'temperatureCelsius', 'TemperatureCelsius'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Soğutucu Türü</label>
                  <div style={s.inputView}>{pick(detailRow, 'standType', 'StandType') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Sayım Tarihi</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'countedAt', 'CountedAt'))}</div>
                </div>
              </div>
              {pick(detailRow, 'photoUrl', 'PhotoUrl') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Fotoğraf</label>
                  <a href={pick(detailRow, 'photoUrl', 'PhotoUrl')} target="_blank" rel="noreferrer"
                     style={{ display: 'block', width: 140, height: 140, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                    <img src={pick(detailRow, 'photoUrl', 'PhotoUrl')} alt="Soğutucu"
                         style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                         onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.style.background = '#f3f4f6'; }} />
                  </a>
                </div>
              )}
              {pick(detailRow, 'notes', 'Notes') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Notlar</label>
                  <div style={s.textareaView}>{pick(detailRow, 'notes', 'Notes')}</div>
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>Ürün Kalemleri ({detailItems?.length ?? '...'})</label>
                {detailLoading && <div style={{ color: '#6b7280', fontSize: 13 }}>Yükleniyor...</div>}
                {!detailLoading && detailItems && (
                  <div style={{
                    border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
                    maxHeight: 320, overflowY: 'auto',
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ background: '#f9fafb' }}>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Ürün</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Yerleşim</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Adet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailItems.length === 0 && (
                          <tr><td colSpan="3" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Kalem yok</td></tr>
                        )}
                        {detailItems.map((item, idx) => (
                          <tr key={pick(item, 'id', 'Id') || idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                              {pick(item, 'productName', 'ProductName') || '-'}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                              {placementLabel(pick(item, 'placementType', 'PlacementType'))}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                              {pick(item, 'quantity', 'Quantity') ?? '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div style={s.modalFooter}>
              <button
                type="button"
                style={s.btnSecondary}
                onClick={() => { setDetailRow(null); setDetailItems(null); }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default StandCountsPageLegacy;
